/**
 * lib/invoiceDelivery.ts
 * ------------------------------------------------------------------
 * One place that turns a freshly-created invoice into a delivered one:
 *   1. render the PDF (invoicePdf) with the school logo,
 *   2. upload it to Storage at invoices/{studentId}/{invoiceId}.pdf,
 *   3. stamp the invoice doc with storagePath + pdfUrl,
 *   4. email it to the parent via the sendInvoiceEmail Cloud Function.
 *
 * Mirrors the HR payslip flow (render → uploadBytes → stamp doc →
 * httpsCallable). Best-effort and non-throwing: callers (Admit wizard,
 * ManualInvoice) invoke this AFTER the invoice itself is safely written,
 * so a PDF / Storage / email hiccup — or the function not being deployed
 * yet — never aborts the invoice or the admission. The outcome is
 * returned so the UI can show an accurate, honest status line.
 * ---------------------------------------------------------------- */
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { storage, functions, db } from '@/lib/firebase';
import { finance } from '@/features/finance/financeWrite';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildInvoicePdf } from '@/lib/invoicePdf';
import { FINANCE_COLLECTIONS, FINANCE_SETTINGS_ID, type Invoice, type Standing } from '@/lib/financeCore';

/** Reads the finance master switch — true unless explicitly turned off. A read
 *  failure defaults to ENABLED so a transient error never silently drops mail. */
async function invoiceEmailEnabled(): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, FINANCE_COLLECTIONS.settings, FINANCE_SETTINGS_ID));
    return !(snap.exists() && (snap.data() as { invoiceEmailEnabled?: boolean }).invoiceEmailEnabled === false);
  } catch {
    return true;
  }
}

export interface DeliverInvoiceResult {
  /** PDF rendered + uploaded + invoice doc stamped. */
  stored: boolean;
  /** sendInvoiceEmail reported a successful send. */
  emailed: boolean;
  /** Public download URL, when stored. */
  pdfUrl?: string;
  /** Why email (or storage) didn't fully succeed — for the status line. */
  reason?: string;
}

export async function deliverInvoice(
  invoice: Invoice,
  opts: {
    toEmail?: string;
    studentName?: string;
    /** The pupil's account at the moment of sending, printed on the PDF
     *  beside the invoice total so the bill is not read as the whole story.
     *  Callers inside the app have the invoices to hand; this module does
     *  not, and fetching them here would put a read in a best-effort path
     *  that is meant never to throw. Omitted = the block is left off. */
    standing?: Standing & { documentCount?: number };
  } = {},
): Promise<DeliverInvoiceResult> {
  const result: DeliverInvoiceResult = { stored: false, emailed: false };

  // 1) render + upload + stamp the invoice doc
  try {
    const logo = await loadLogoDataUrl();
    const blob = buildInvoicePdf(invoice, { logoDataUrl: logo, standing: opts.standing }).output('blob');
    const path = `invoices/${invoice.studentId}/${invoice.id}.pdf`;
    const sref = ref(storage, path);
    await uploadBytes(sref, blob, { contentType: 'application/pdf' });
    const url = await getDownloadURL(sref);
    await finance.invoices.update(invoice.id, { storagePath: path, pdfUrl: url });
    result.stored = true;
    result.pdfUrl = url;
  } catch (e) {
    // Without a stored PDF the function has nothing to attach — stop here.
    result.reason = `PDF/storage failed: ${(e as Error).message}`;
    return result;
  }

  // 2) email the parent (best-effort) — unless the school has temporarily
  //    turned invoice emailing off. The PDF is already stored either way.
  if (!(await invoiceEmailEnabled())) {
    result.reason = 'Invoice emailing is currently turned OFF — PDF saved, not emailed.';
    return result;
  }
  const to = (opts.toEmail ?? '').trim();
  if (!to) {
    result.reason = 'No parent email on file — PDF saved, not emailed.';
    return result;
  }
  try {
    const fn = httpsCallable(functions, 'sendInvoiceEmail');
    const res = (await fn({
      invoiceId: invoice.id,
      toEmail: to,
      studentName: opts.studentName ?? invoice.studentName ?? '',
      invoiceNo: invoice.invoiceNo,
    })) as { data?: { ok?: boolean; reason?: string } };
    if (res?.data?.ok) result.emailed = true;
    else result.reason = res?.data?.reason ?? 'Email not sent.';
  } catch (e) {
    // Most common pre-deploy: function not found. Keep it non-fatal.
    result.reason = `Invoice saved; email not sent (${(e as Error).message}).`;
  }
  return result;
}
